import { useEffect, useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SubmitButton } from "@/components/form/SubmitButton";
import { authApi } from "@/features/auth/api";
import { formatApiError } from "@/features/auth/formatError";

type Props = {
    email: string;
    purpose: "login" | "reset";
    loading: boolean;
    onVerify: (code: string) => Promise<void>;
    onChangeEmail: () => void;
    testIdPrefix: string;
};

export function OtpCodeStep({ email, purpose, loading, onVerify, onChangeEmail, testIdPrefix }: Props) {
    const [code, setCode] = useState("");
    const [cooldown, setCooldown] = useState(60);
    const [resending, setResending] = useState(false);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const resend = async () => {
        setResending(true);
        try {
            await authApi.sendOtp(email, purpose);
            setCooldown(60);
            setCode("");
            toast.success("A new code has been sent");
        } catch (err) {
            toast.error(formatApiError(err, "Could not resend the code"));
        } finally {
            setResending(false);
        }
    };

    return (
        <form
            noValidate
            className="space-y-5"
            onSubmit={(e) => {
                e.preventDefault();
                if (!/^\d{6}$/.test(code)) {
                    toast.error("Enter the 6-digit code from your email");
                    return;
                }
                void onVerify(code);
            }}
        >
            <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-otp-hint`}>
                We sent a 6-digit code to{" "}
                <span className="font-semibold text-foreground">{email}</span>. It expires in 5 minutes.
            </p>
            <div className="space-y-1.5">
                <Label htmlFor={`${testIdPrefix}-otp-code`}>Verification code</Label>
                <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        id={`${testIdPrefix}-otp-code`}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="123456"
                        className="h-11 pl-9 text-lg tracking-[0.4em]"
                        data-testid={`${testIdPrefix}-otp-code`}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        disabled={loading}
                        autoFocus
                    />
                </div>
            </div>
            <SubmitButton
                type="submit"
                loading={loading}
                loadingText="Verifying…"
                data-testid={`${testIdPrefix}-otp-verify`}
            >
                Verify code
            </SubmitButton>
            <div className="flex items-center justify-between text-sm">
                <button
                    type="button"
                    onClick={onChangeEmail}
                    className="font-medium text-muted-foreground hover:text-brand"
                    data-testid={`${testIdPrefix}-otp-change-email`}
                >
                    Use a different email
                </button>
                <button
                    type="button"
                    onClick={resend}
                    disabled={cooldown > 0 || resending}
                    className="font-semibold text-brand disabled:text-muted-foreground disabled:no-underline hover:underline"
                    data-testid={`${testIdPrefix}-otp-resend`}
                >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
                </button>
            </div>
        </form>
    );
}
